/**
 * ONE TAP — client WebSocket transport.
 *
 * Responsibilities, in order of how much they matter to how the game feels:
 *
 *  1. Clock sync. Everything downstream (snapshot interpolation, lag comp) is
 *     expressed in *server* time, so `serverTimeOffsetMs` is load-bearing. It is
 *     estimated from a rolling window of ping/pong samples with outlier rejection
 *     and a median, then slewed rather than snapped — one bad sample must never
 *     jerk the render clock, because a jerked render clock is visible as every
 *     remote player teleporting at once.
 *  2. Survivability. Sockets die on mobile, on Wi-Fi handoff, and whenever a
 *     Durable Object hibernates. We reconnect with jittered exponential backoff
 *     and replay the `hello` handshake automatically.
 *  3. Typed, boring message passing.
 *
 * The URL is relative (`/api/room/ONETAP-XXXX/ws`) and upgraded to ws:// or
 * wss:// from `location`, so dev (Vite proxy) and prod behave identically.
 */

import { CLIENT_TIMEOUT_MS, HEARTBEAT_MS } from '@shared/constants';
import type { ClientMessage, ServerMessage } from '@shared/types';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'closed';

// ---------------------------------------------------------------------------
// Transport tuning. These govern the socket, not gameplay — gameplay numbers all
// live in @shared/constants. The two that do come from there (HEARTBEAT_MS,
// CLIENT_TIMEOUT_MS) are the contract with the server's own liveness checks.
// ---------------------------------------------------------------------------

/** Steady-state clock-sync cadence. Five samples per server heartbeat window. */
const PING_INTERVAL_MS = HEARTBEAT_MS / 5;
/** On connect we burst pings so the clock locks in well under a second. */
const PING_BURST_COUNT = 6;
const PING_BURST_INTERVAL_MS = 180;
/** Scheduler granularity — one timer drives pings, the burst, and the watchdog. */
const TIMER_INTERVAL_MS = 100;

/** Rolling window of ping/pong samples the median is taken over. Odd on purpose. */
const CLOCK_WINDOW = 11;
/** Samples slower than `min * factor + pad` are queue-delayed garbage; drop them. */
const RTT_OUTLIER_FACTOR = 2.5;
const RTT_OUTLIER_PAD_MS = 25;
/** Anything above this is a stalled tab, not a network sample. */
const MAX_PLAUSIBLE_RTT_MS = 4000;
/** Disagreement larger than this is a real clock jump (tab sleep) — snap, don't slew. */
const CLOCK_SNAP_MS = 250;
/** Fraction of the remaining clock error corrected per accepted sample. */
const CLOCK_SLEW = 0.35;

const BACKOFF_BASE_MS = 400;
const BACKOFF_MAX_MS = 8000;
const BACKOFF_JITTER = 0.3;
const MAX_RECONNECT_ATTEMPTS = 10;

/** Messages buffered while the socket is down. Inputs are never queued — they rot. */
const SEND_QUEUE_MAX = 32;

/**
 * Monotonic wall-clock in the same epoch as Date.now(), but immune to the system
 * clock being adjusted mid-match. RTT measured against a clock that can step is
 * how you get a 3-second "latency" spike out of nowhere.
 */
const TIME_ORIGIN =
  typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number'
    ? performance.timeOrigin
    : Date.now();

function nowMs(): number {
  return typeof performance !== 'undefined' ? TIME_ORIGIN + performance.now() : Date.now();
}

interface ClockSample {
  rtt: number;
  offset: number;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) * 0.5;
}

/** Turn a raw WebSocket close code into something a human can read in the UI. */
function describeClose(code: number, reason: string): string {
  if (reason) return reason;
  switch (code) {
    case 1000:
      return 'Disconnected.';
    case 1001:
      return 'Server going away.';
    case 1005:
      return 'Connection closed.';
    case 1006:
      return 'Connection lost.';
    case 1011:
      return 'Server error.';
    case 1012:
      return 'Server restarting.';
    case 1013:
      return 'Server busy, try again.';
    default:
      return `Connection closed (${code}).`;
  }
}

/** Resolve a relative path against the page origin, upgrading http(s) -> ws(s). */
function resolveWsUrl(url: string): string {
  if (/^wss?:\/\//i.test(url)) return url;
  if (/^https?:\/\//i.test(url)) return url.replace(/^http/i, 'ws');
  if (typeof location === 'undefined') return url;
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${scheme}//${location.host}${path}`;
}

export class Connection {
  /** One-way trip time estimate, in ms. Median-filtered; safe to render directly. */
  latencyMs = 0;
  /** Add this to local wall-clock to get server time. Slewed, never jerked. */
  serverTimeOffsetMs = 0;

  onMessage: (m: ServerMessage) => void = () => {};
  onClose: (reason: string) => void = () => {};
  /**
   * Additive to the fixed contract: lets the UI mirror the socket state without
   * polling. Fires on every transition, including reconnect attempts.
   */
  onStatus: (s: ConnectionStatus, detail?: string) => void = () => {};

  readonly url: string;

  private ws: WebSocket | null = null;
  private _status: ConnectionStatus = 'idle';

  private timer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private attempt = 0;
  private userClosed = false;
  private connectResolve: (() => void) | null = null;
  private connectReject: ((e: Error) => void) | null = null;
  private connectPromise: Promise<void> | null = null;

  private readonly samples: ClockSample[] = [];
  private clockPrimed = false;
  private pendingPings = 0;
  private lastPingAt = 0;
  private burstRemaining = 0;
  private lastRecvAt = 0;

  /** The handshake is replayed verbatim after a reconnect so the DO re-seats us. */
  private hello: ClientMessage | null = null;
  private readonly queue: ClientMessage[] = [];

  constructor(url: string) {
    this.url = resolveWsUrl(url);
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  /** Estimated current server time. This is the clock every net timestamp lives in. */
  serverNowMs(): number {
    return nowMs() + this.serverTimeOffsetMs;
  }

  /** True once at least one pong has landed and the clock is usable. */
  get clockReady(): boolean {
    return this.clockPrimed;
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Resolves the first time the socket opens. If the very first attempt fails we
   * keep retrying with backoff and only reject once the attempts are exhausted,
   * so a cold-starting Durable Object doesn't read as an error to the player.
   */
  connect(): Promise<void> {
    if (this._status === 'connected') return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    this.userClosed = false;
    this.attempt = 0;
    this.connectPromise = new Promise<void>((resolve, reject) => {
      this.connectResolve = resolve;
      this.connectReject = reject;
    });
    this.open();
    return this.connectPromise;
  }

  send(m: ClientMessage): void {
    if (m.t === 'hello') this.hello = m;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(m));
      } catch {
        // A send failure means the socket is already gone; the close handler
        // will pick it up. Queue anything worth replaying.
        this.enqueue(m);
      }
      return;
    }
    this.enqueue(m);
  }

  close(): void {
    this.userClosed = true;
    this.clearTimers();
    const sock = this.ws;
    this.ws = null;
    if (sock) {
      sock.onopen = null;
      sock.onmessage = null;
      sock.onerror = null;
      sock.onclose = null;
      try {
        sock.close(1000, 'client closed');
      } catch {
        /* already closing */
      }
    }
    this.queue.length = 0;
    this.setStatus('closed');
    this.settleReject(new Error('Connection closed.'));
  }

  // -------------------------------------------------------------------------
  // Socket plumbing
  // -------------------------------------------------------------------------

  private open(): void {
    if (this.userClosed) return;
    this.setStatus('connecting', this.attempt > 0 ? `Reconnecting (${this.attempt})…` : undefined);

    let sock: WebSocket;
    try {
      sock = new WebSocket(this.url);
    } catch (err) {
      this.scheduleReconnect(err instanceof Error ? err.message : 'Could not open socket.');
      return;
    }
    this.ws = sock;

    sock.onopen = () => {
      if (this.ws !== sock) return;
      this.attempt = 0;
      this.lastRecvAt = nowMs();
      this.pendingPings = 0;
      this.burstRemaining = PING_BURST_COUNT;
      this.lastPingAt = 0;
      this.setStatus('connected');
      this.startTimers();

      // Re-handshake, then drain anything that piled up while we were down.
      if (this.hello) this.rawSend(this.hello);
      const pending = this.queue.splice(0, this.queue.length);
      for (const m of pending) this.rawSend(m);

      this.ping();
      this.settleResolve();
    };

    sock.onmessage = (ev: MessageEvent) => {
      if (this.ws !== sock) return;
      this.lastRecvAt = nowMs();
      if (typeof ev.data !== 'string') return;

      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data) as ServerMessage;
      } catch {
        return;
      }
      if (!msg || typeof (msg as { t?: unknown }).t !== 'string') return;

      if (msg.t === 'pong') {
        this.onPong(msg.sent, msg.serverTimeMs);
        return; // Clock plumbing; nothing above the transport cares.
      }

      if (msg.t === 'kick') {
        // A kick is authoritative and final — do not fight it with a reconnect.
        this.userClosed = true;
        this.onMessage(msg);
        this.clearTimers();
        try {
          sock.close(1000, 'kicked');
        } catch {
          /* ignore */
        }
        this.ws = null;
        this.setStatus('closed', msg.reason);
        this.onClose(msg.reason);
        this.settleReject(new Error(msg.reason));
        return;
      }

      this.onMessage(msg);
    };

    sock.onerror = () => {
      // `onerror` is always followed by `onclose`; let close do the work so we
      // only ever run one reconnect path.
      if (this.ws === sock) this.setStatus('connecting', 'Network error…');
    };

    sock.onclose = (ev: CloseEvent) => {
      if (this.ws !== sock) return;
      this.ws = null;
      this.clearTimers();
      const reason = describeClose(ev.code, ev.reason);
      if (this.userClosed) {
        this.setStatus('closed', reason);
        return;
      }
      this.onClose(reason);
      this.scheduleReconnect(reason);
    };
  }

  private rawSend(m: ClientMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(m));
    } catch {
      /* the close handler will deal with it */
    }
  }

  private enqueue(m: ClientMessage): void {
    // Inputs and pings are worthless once stale: an input replayed seconds later
    // would be rejected by the server anyway, and a stale ping poisons the clock.
    if (m.t === 'input' || m.t === 'ping') return;
    if (m.t === 'hello') return; // already stored; replayed on open
    if (this.queue.length >= SEND_QUEUE_MAX) this.queue.shift();
    this.queue.push(m);
  }

  private scheduleReconnect(reason: string): void {
    if (this.userClosed) return;
    if (this.attempt >= MAX_RECONNECT_ATTEMPTS) {
      this.setStatus('error', reason);
      this.settleReject(new Error(reason));
      return;
    }
    const exp = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * Math.pow(2, this.attempt));
    // Jitter keeps a room full of players from stampeding a waking DO in lockstep.
    const delay = exp * (1 - BACKOFF_JITTER + Math.random() * BACKOFF_JITTER * 2);
    this.attempt++;
    this.setStatus('connecting', reason);
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private setStatus(s: ConnectionStatus, detail?: string): void {
    if (this._status === s && !detail) return;
    this._status = s;
    this.onStatus(s, detail);
  }

  private settleResolve(): void {
    const resolve = this.connectResolve;
    this.connectResolve = null;
    this.connectReject = null;
    this.connectPromise = null;
    if (resolve) resolve();
  }

  private settleReject(err: Error): void {
    const reject = this.connectReject;
    this.connectResolve = null;
    this.connectReject = null;
    this.connectPromise = null;
    if (reject) reject(err);
  }

  // -------------------------------------------------------------------------
  // Clock sync + liveness
  // -------------------------------------------------------------------------

  private startTimers(): void {
    if (this.timer !== null) return;
    this.timer = setInterval(() => this.tick(), TIMER_INTERVAL_MS);
  }

  private clearTimers(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private tick(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const t = nowMs();

    const interval = this.burstRemaining > 0 ? PING_BURST_INTERVAL_MS : PING_INTERVAL_MS;
    if (t - this.lastPingAt >= interval) this.ping();

    // Watchdog: the server heartbeats, so silence means the socket is a corpse
    // that hasn't fallen over yet. Kill it and let the backoff path reconnect.
    if (this.lastRecvAt > 0 && t - this.lastRecvAt > CLIENT_TIMEOUT_MS) {
      const sock = this.ws;
      try {
        sock.close(4000, 'timeout');
      } catch {
        /* ignore */
      }
      // Drive the reconnect ourselves rather than waiting on `onclose`, which
      // some browsers delay indefinitely after a sleep/wake. The late `onclose`
      // is then ignored by the `this.ws !== sock` guard, so we never double-fire.
      if (this.ws === sock) {
        this.ws = null;
        this.clearTimers();
        this.onClose('Connection timed out.');
        this.scheduleReconnect('Connection timed out.');
      }
    }
  }

  private ping(): void {
    this.lastPingAt = nowMs();
    if (this.burstRemaining > 0) this.burstRemaining--;
    this.pendingPings++;
    this.rawSend({ t: 'ping', sent: this.lastPingAt });
  }

  private onPong(sent: number, serverTimeMs: number): void {
    if (this.pendingPings > 0) this.pendingPings--;
    const t = nowMs();
    const rtt = t - sent;
    if (!Number.isFinite(rtt) || rtt < 0 || rtt > MAX_PLAUSIBLE_RTT_MS) return;
    if (!Number.isFinite(serverTimeMs)) return;

    // Assume symmetric latency: the server stamped the reply half an RTT ago.
    const offset = serverTimeMs + rtt * 0.5 - t;

    this.samples.push({ rtt, offset });
    if (this.samples.length > CLOCK_WINDOW) this.samples.shift();

    // Reject the tail: a sample that sat in a router queue reports a real RTT but
    // a badly biased offset, and it is exactly the sample that would jerk the clock.
    let minRtt = Infinity;
    for (const s of this.samples) if (s.rtt < minRtt) minRtt = s.rtt;
    const cutoff = minRtt * RTT_OUTLIER_FACTOR + RTT_OUTLIER_PAD_MS;

    const rtts: number[] = [];
    const offsets: number[] = [];
    for (const s of this.samples) {
      if (s.rtt > cutoff) continue;
      rtts.push(s.rtt);
      offsets.push(s.offset);
    }
    if (rtts.length === 0) return;

    this.latencyMs = median(rtts) * 0.5;
    const target = median(offsets);

    if (!this.clockPrimed || Math.abs(target - this.serverTimeOffsetMs) > CLOCK_SNAP_MS) {
      // First lock, or the local clock genuinely stepped (tab sleep/wake).
      this.serverTimeOffsetMs = target;
      this.clockPrimed = true;
    } else {
      this.serverTimeOffsetMs += (target - this.serverTimeOffsetMs) * CLOCK_SLEW;
    }
  }
}
